import { get } from 'lodash';
import { Box, Text, Heading, SpinnerSize, Spinner, Button, Icon, ButtonSize } from '@bechtel/gds';
import React, { FC, Suspense, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { SuspenseLoader, ErrorBoundary } from '@bechtel/client-common';
import { ReportDetailContainerStyled, ReportContainerStyled, ReportHeaderRowStyled } from './styled';
import { ReportsDetailProps } from './types';
import { Trans } from '@lingui/macro';
import { useLazyQuery } from '@apollo/client';
import { GET_REPORT_BY_ID } from '@bechtel/client-gql';
import { ProjectReport } from '@bechtel/types';
import { ReactComponent as PackageIcon } from '@bechtel/client-assets/dist/images/work-package.svg';
interface RouteParams {
  id: string;
}

export const ReportsDetail: FC<ReportsDetailProps> = ({ isListCollapsed }) => {
  const { id } = useParams<RouteParams>();
  const decodedId = id && id !== 'reports' ? decodeURIComponent(id) : '';
  const [loadReportByIds, { loading, error, data, refetch }] = useLazyQuery<{
    reportByIds: ProjectReport[];
  }>(GET_REPORT_BY_ID, {
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'network-only',
  });

  const reportData: ProjectReport[] = get(data, 'reportByIds', []);
  const reportListById = reportData.length > 0 ? reportData[0] : undefined;
  useEffect(() => {
    if (!decodedId) return;
    loadReportByIds({
      variables: {
        ids: [decodedId],
      },
    });
  }, [decodedId, loadReportByIds]);

  return (
    <ReportDetailContainerStyled isListCollapsed={isListCollapsed}>
      {error && (
        <Box flexDirection={{ sm: 'column' }} alignItems={{ sm: 'center' }} pt={{ sm: '900' }} mt={{ sm: '500' }}>
          <Box mt={{ sm: '700' }} px={{ sm: '600' }}>
            <Text>
              <Trans>There was a problem loading the Reports.</Trans>
            </Text>
            <Text>
              <Trans> Please click the button below to try again.</Trans>
            </Text>
          </Box>
          <Box mt={{ sm: '400' }}>
            <Button size={ButtonSize.MEDIUM} onClick={() => refetch()}>
              <Box mr={{ sm: '400' }}>
                <Icon icon="undo" />
              </Box>
              Retry
            </Button>
          </Box>
        </Box>
      )}
      {!error && loading && (
        <Box
          flexGrow={{ sm: 1 }}
          pt={{ sm: '900' }}
          mt={{ sm: '500' }}
          alignItems={{ sm: 'center' }}
          flexDirection={{ sm: 'column' }}
          justifyContent={{ sm: 'center' }}
          data-testid="loading"
        >
          <Spinner size={SpinnerSize.LARGE} />
        </Box>
      )}
      {!error && !loading && !reportListById && (
        <ErrorBoundary>
          <Suspense fallback={<SuspenseLoader></SuspenseLoader>}>
            <ReportContainerStyled>
              <ReportHeaderRowStyled>
                <Heading size="600" color="white" weight="600">
                  <Trans> No Report Selected</Trans>
                </Heading>
              </ReportHeaderRowStyled>
              <Box flexDirection={{ sm: 'column' }} alignItems={{ sm: 'center' }} pt={{ sm: '900' }} mt={{ sm: '500' }}>
                <PackageIcon />
                <Box mt={{ sm: '700' }} px={{ sm: '600' }}>
                  <Text size="400" family="graphik" color="grey.600">
                    <Trans>Please select a report</Trans>
                  </Text>
                </Box>
              </Box>
            </ReportContainerStyled>
          </Suspense>
        </ErrorBoundary>
      )}

      {!error && !loading && reportListById && (
        <ErrorBoundary>
          <Suspense fallback={<SuspenseLoader></SuspenseLoader>}>
            <ReportContainerStyled>
              <ReportHeaderRowStyled>
                <Heading size="600" color="white" weight="600">
                  <Trans>{reportListById.name}</Trans>
                </Heading>
              </ReportHeaderRowStyled>

              <Box flexDirection={{ sm: 'column' }} style={{ height: '100%' }}>
                <Box px={{ sm: '600' }} flexGrow={{ sm: 1 }}>
                  <Box flexGrow={{ sm: 1 }} style={{ position: 'relative' }}>
                    <iframe
                      width="100%"
                      scrolling="no"
                      frameBorder="0"
                      src={decodeURIComponent(reportListById.powerbiUrl)}
                    ></iframe>
                  </Box>
                </Box>
              </Box>
            </ReportContainerStyled>
          </Suspense>
        </ErrorBoundary>
      )}
    </ReportDetailContainerStyled>
  );
};
